import { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import styles from './Finding.module.scss';
import { InspectionsFindingDto, InspectionsFindingsItemDto } from '../../api/types/dtos/inspection';
import { ProgramVersionItemDto } from '../../api/types/dtos/program';
import SearchCriteriaModal from './SearchCriteriaModal';
import ItemDetailsModal from './ItemDetailsModal';
import { useOpenAI } from '../../api/hooks/useOpenAI';
import { createProgramVersionItemService } from '../../api/services/program-version-item.service';

interface FindingProps {
  finding: InspectionsFindingDto;
  onUpdate: (finding: InspectionsFindingDto) => void;
  onAddFindingItem: (findingId: string, item: Omit<InspectionsFindingsItemDto, 'findingId'>) => void;
  onRemoveFindingItem: (findingId: string, itemId: string) => void;
  organizationId: string;
  programId: string;
  versionId: string;
}

export const Finding: React.FC<FindingProps> = ({
  finding,
  onUpdate,
  onAddFindingItem,
  onRemoveFindingItem,
  organizationId,
  programId,
  versionId
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedFinding, setEditedFinding] = useState(finding);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [programVersionItems, setProgramVersionItems] = useState<ProgramVersionItemDto[]>([]);
  const [selectedItem, setSelectedItem] = useState<ProgramVersionItemDto | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [_isLoading, setIsLoading] = useState(false);
  const { isLoading: isAILoading, error, suggestedCriteria, getSuggestedCriteria } = useOpenAI();

  // Load program version items on component mount
  useEffect(() => {
    const loadProgramVersionItems = async () => {
      setIsLoading(true);
      try {
        const service = createProgramVersionItemService(organizationId, programId, versionId);
        const response = await service.findAll();
        setProgramVersionItems(response.data.sort((a, b) => {
          const aNum = parseFloat(a.cpNumber || '0');
          const bNum = parseFloat(b.cpNumber || '0');
          return aNum - bNum;
        }));
      } catch (error) {
        console.error('Error loading program version items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgramVersionItems();
  }, [organizationId, programId, versionId]);

  // Get suggested criteria when finding content changes
  useEffect(() => {
    if (finding.finding) {
      getSuggestedCriteria(finding.finding);
    }
  }, [finding.finding, getSuggestedCriteria]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    onUpdate(editedFinding);
    setIsEditing(false);
  };

  const handleAddCriteria = (item: Omit<InspectionsFindingsItemDto, 'findingId'>) => {
    if (finding.id) {
      onAddFindingItem(finding.id, item);
    }
  };

  const handleRemoveCriteria = (itemId: string) => {
    if (finding.id) {
      onRemoveFindingItem(finding.id, itemId);
    }
  };

  const handleFieldChange = (field: keyof InspectionsFindingDto, value: any) => {
    setEditedFinding(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddSelectedItems = (selectedItems: ProgramVersionItemDto[]) => {
    selectedItems.forEach(item => {
      handleAddCriteria({
        id: undefined,
        item: {
        isActive: true,
          id: item.id,
          cpNumber: item.cpNumber,
          description: item.description,
          name: item.name,
          versionId: item.versionId,
          programLevelId: item.programLevelId,
          measurementTypeId: item.measurementTypeId
        },
        isActive: true
      });
    });
  };

  const handleItemClick = (itemId: string) => {
    const item = programVersionItems.find(item => item.id === itemId);
    if (item) {
      setSelectedItem(item);
      setIsDetailsModalOpen(true);
    }
  };

  const getItemDetails = (itemId: string) => {
    return programVersionItems.find(item => item.id === itemId);
  };

  // Filter to show only active criteria items
  const activeCriteriaItems = finding.inspectionsFindingsItems?.filter(item => item.isActive) || [];

  // Filter out suggested criteria that are already in active criteria
  const filteredSuggestedCriteria = suggestedCriteria?.filter(suggestedItem => {
    return !activeCriteriaItems.some(activeItem => 
      activeItem.item?.cpNumber === suggestedItem.item?.cpNumber
    );
  }) || [];

  const handleRefreshSuggestions = () => {
    if (finding.finding) {
      getSuggestedCriteria(finding.finding);
    }
  };

  return (
    <div className={styles.finding}>
      <div className={styles.row}>
        <div className={styles.column}>
          <div className={styles.columnHeader}>
            <h3>Criterios</h3>
            <IconButton 
              size="small" 
              onClick={() => setIsSearchModalOpen(true)}
              className={styles.searchButton}
            >
              <SearchIcon />
            </IconButton>
          </div>
          <div className={styles.criteriaList}>
            {activeCriteriaItems.map(item => {
              const details = getItemDetails(item.item?.id || '');
              return (
                <div key={item.id} className={styles.criteriaItem}>
                  <Tooltip title={details?.name || ''}>
                    <IconButton
                      size="small"
                      onClick={() => handleItemClick(item.item?.id || '')}
                      className={styles.infoIcon}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                  <IconButton 
                    size="small" 
                    onClick={() => item.id && handleRemoveCriteria(item.id)}
                    className={styles.criteriaIcon}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <span>LM {item.item?.cpNumber}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.columnHeader}>
            <h3>Sugerencia PapoAI</h3>
            <IconButton 
              size="small" 
              onClick={handleRefreshSuggestions}
              disabled={isAILoading}
              className={styles.refreshButton}
            >
              <RefreshIcon />
            </IconButton>
          </div>
          <div className={styles.criteriaList}>
            {isAILoading ? (
              <div className={styles.loadingContainer}>
                <CircularProgress size={24} />
                <span>Analizando hallazgo...</span>
              </div>
            ) : error ? (
              <Alert severity="error" className={styles.error}>
                {error.message}
              </Alert>
            ) : (
              filteredSuggestedCriteria.map(item => {
                const details = getItemDetails(item.item?.id || '');
                return (
                  <div key={item.id || item.item?.id} className={styles.criteriaItem}>
                    <Tooltip title={details?.name || ''}>
                      <IconButton
                        size="small"
                        onClick={() => handleItemClick(item.item?.id || '')}
                        className={styles.infoIcon}
                      >
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton 
                      size="small" 
                      onClick={() => handleAddCriteria(item)}
                      className={styles.criteriaIcon}
                    >
                      <AddIcon />
                    </IconButton>
                    <span>LM {item.item?.cpNumber}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className={styles.column}>
          <h3>Hallazgos</h3>
          {isEditing ? (
            <TextField
              multiline
              value={editedFinding.finding}
              onChange={(e) => handleFieldChange('finding', e.target.value)}
            />
          ) : (
            <p>{finding.finding}</p>
          )}
        </div>

        <div className={styles.column}>
          <h3>Análisis</h3>
          {isEditing ? (
            <TextField
              multiline
              value={editedFinding.analysis || ''}
              onChange={(e) => handleFieldChange('analysis', e.target.value)}
            />
          ) : (
            <p>{finding.analysis}</p>
          )}
        </div>

        <div className={styles.column}>
          <h3>Acción Correctiva</h3>
          {isEditing ? (
            <TextField
              multiline
              value={editedFinding.correctiveAction || ''}
              onChange={(e) => handleFieldChange('correctiveAction', e.target.value)}
            />
          ) : (
            <p>{finding.correctiveAction}</p>
          )}
        </div>

        <div className={styles.column}>
          <h3>Acción Preventiva</h3>
          {isEditing ? (
            <TextField
              multiline
              value={editedFinding.preventiveAction || ''}
              onChange={(e) => handleFieldChange('preventiveAction', e.target.value)}
            />
          ) : (
            <p>{finding.preventiveAction}</p>
          )}
        </div>

        <div className={styles.column}>
          <h3>Fecha de Cierre</h3>
          {isEditing ? (
            <TextField
              type="date"
              value={editedFinding.closeDate ? new Date(editedFinding.closeDate).toISOString().split('T')[0] : ''}
              onChange={(e) => handleFieldChange('closeDate', new Date(e.target.value).getTime())}
            />
          ) : (
            <p>{finding.closeDate ? new Date(finding.closeDate).toLocaleDateString() : ''}</p>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <IconButton onClick={() => {/* TODO: Implement files modal */}}>
          <FolderIcon />
        </IconButton>
        {isEditing ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckIcon />}
            onClick={handleSave}
          >
            Save
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Modificar
          </Button>
        )}
      </div>

      <SearchCriteriaModal
        open={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onAdd={handleAddSelectedItems}
        organizationId={organizationId}
        programId={programId}
        versionId={versionId}
        existingItems={programVersionItems}
      />

      <ItemDetailsModal
        open={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
      />
    </div>
  );
};

export default Finding; 